import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';

export interface OrganizationMemberActionModalProps {
  title: string;
  description: string;
  confirmLabel: string;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
}

export function OrganizationMemberActionModal({
  title,
  description,
  confirmLabel,
  submitting = false,
  error = null,
  onClose,
  onConfirm,
}: OrganizationMemberActionModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !submitting) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, submitting]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="organization-member-action-title"
        className="w-full max-w-md rounded-xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-4">
          <div>
            <h2
              id="organization-member-action-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              {title}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-300"
            aria-label="Close"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="px-6 py-5">
          <p className="text-body text-neutral-700">{description}</p>
          {error ? <p className="mt-4 text-small text-red-700">{error}</p> : null}
        </div>

        <div className="flex justify-end gap-3 border-t border-neutral-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-neutral-200 px-4 py-2 text-small font-semibold text-neutral-700 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:text-neutral-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={submitting}
            className="focus-ring inline-flex items-center rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:bg-backoffice-300"
          >
            {submitting ? (
              <span className="inline-flex items-center whitespace-nowrap">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                &nbsp;{confirmLabel}
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
